import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from 'html5-qrcode';

// Camera barcode scanner (EAN/UPC) in a modal, styled with the app theme. Calls onDetect(code)
// once with the first decoded barcode, then stops. Needs HTTPS + a real device camera; on a
// desktop without one it shows the permission/no-camera message and the caller falls back to
// manual search. Formats limited to product barcodes so QR codes etc. are ignored.
const FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
];

const REGION_ID = 'nutrition-barcode-region';

// Camera hints for getUserMedia — passed as `videoConstraints`, which (when present) REPLACES
// the plain `{facingMode}` object below entirely, so facingMode has to be repeated in here too.
//  - width/height "ideal" 1280x720: without this, the browser is free to hand back whatever
//    resolution it likes for an unconstrained request — on a lot of phones that's the camera's
//    full sensor resolution (well past what a printed barcode needs to be legible), which the
//    decoder then has to scan every single frame. 720p is plenty for a barcode filling a good
//    part of the frame at normal scanning distance, at a fraction of the pixels to process.
//  - advanced.focusMode: 'continuous': best-effort — per spec, an unsupported entry inside
//    `advanced` is silently skipped rather than failing getUserMedia, so this is safe to
//    request everywhere even though (notably) iOS Safari ignores it. A camera stuck hunting for
//    focus (or locked on the wrong distance) is the single biggest cause of "never recognises
//    the barcode" on a phone, since every attempt decodes a blurred frame.
const VIDEO_CONSTRAINTS = {
  facingMode: 'environment',
  width: { ideal: 1280 },
  height: { ideal: 720 },
  advanced: [{ focusMode: 'continuous' }],
};

// Region of interest: crops each frame down to just the box drawn on screen before either
// decoder (native BarcodeDetector or the zxing-js fallback) ever looks at it — html5-qrcode
// uses this function's return value both to draw the on-screen guide box AND to size/position
// the small canvas it feeds to whichever decoder is active, so the full camera frame is never
// scanned, only the cropped region within the box.
const QRBOX = (vw, vh) => {
  const w = Math.max(120, Math.floor(Math.min(vw, vh) * 0.8));
  return { width: w, height: Math.floor(w * 0.55) };
};

// html5-qrcode's stop() THROWS SYNCHRONOUSLY (a string) when the scanner isn't in the SCANNING
// state — so a plain `.stop().catch()` doesn't help, and an unguarded call from an effect
// cleanup during unmount tears the whole React tree down. This wrapper never throws.
async function safeStop(scanner) {
  if (!scanner) return;
  try {
    if (typeof scanner.getState === 'function' && scanner.getState() === Html5QrcodeScannerState.SCANNING) {
      await scanner.stop();
    }
  } catch { /* already stopped / mid-teardown */ }
  try { if (typeof scanner.clear === 'function') scanner.clear(); } catch { /* ignore */ }
}

// Last-resort guard: if the scan library throws during render/commit, degrade to a message +
// close instead of unmounting the whole Food Tracker page.
class ScannerErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error('BarcodeScanner crashed:', err); }
  render() {
    if (this.state.failed) {
      return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={this.props.onClose}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, padding: 20, maxWidth: 320, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: '#f59e0b', marginBottom: 12 }}>The scanner stopped unexpectedly. Search for the food manually instead.</div>
            <button onClick={this.props.onClose} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function BarcodeScanner(props) {
  return (
    <ScannerErrorBoundary onClose={props.onClose}>
      <BarcodeScannerInner {...props} />
    </ScannerErrorBoundary>
  );
}

function BarcodeScannerInner({ onDetect, onClose }) {
  const scannerRef = useRef(null);
  const doneRef = useRef(false);
  const onDetectRef = useRef(onDetect);
  onDetectRef.current = onDetect;
  const [error, setError] = useState('');

  // Mount-once: start the camera when the modal opens, stop it on close. onDetect is read via
  // a ref so a re-rendered parent callback doesn't restart the camera.
  useEffect(() => {
    // `useBarCodeDetectorIfSupported` already DEFAULTS to true in this library version — a
    // browser with the native BarcodeDetector API (hardware-accelerated, notably faster than
    // any JS decoder) uses it automatically, falling back to html5-qrcode's own zxing-js
    // decoder only where it's unavailable (older Safari in particular). Passed explicitly here
    // so that behaviour is documented and doesn't depend on an unstated library default.
    const scanner = new Html5Qrcode(REGION_ID, {
      formatsToSupport: FORMATS,
      useBarCodeDetectorIfSupported: true,
      verbose: false,
    });
    scannerRef.current = scanner;
    let cancelled = false;

    const onSuccess = (decodedText, result) => {
      if (doneRef.current) return;
      doneRef.current = true;
      // Which engine actually decoded this (native 'BarcodeDetector' vs the 'zxing-js'
      // fallback) — check via a connected phone's remote devtools console when verifying this
      // on-device; not shown in the UI itself so as not to clutter it for players.
      const engine = result && result.result && result.result.debugData && result.result.debugData.decoderName;
      if (engine) console.log(`[BarcodeScanner] decoded via ${engine}`);
      const code = String(decodedText).trim();
      safeStop(scannerRef.current).finally(() => onDetectRef.current(code));
    };
    const onFrameError = () => {}; // per-frame "no code" — ignore

    const start = (videoConstraints) => scanner.start(
      { facingMode: 'environment' }, // ignored once videoConstraints is set below; kept as the pre-start default
      { fps: 10, qrbox: QRBOX, videoConstraints },
      onSuccess,
      onFrameError
    );

    start(VIDEO_CONSTRAINTS).catch(() => {
      if (cancelled) return;
      // Per spec, an unsupported entry inside `advanced` should be ignored rather than
      // rejecting getUserMedia — but not every device/browser honours that. Retry once with
      // nothing but facingMode before treating this as a real camera/permission failure, so a
      // stricter resolution/focus hint can never be the reason the scanner refuses to open.
      start({ facingMode: 'environment' }).catch((err) => {
        if (cancelled) return;
        const msg = String(err && err.message ? err.message : err);
        setError(
          /permission|NotAllowed/i.test(msg) ? 'No camera access. Allow the camera, or use search instead.'
          : /NotFound|no camera|NotReadable|in use/i.test(msg) ? 'No usable camera on this device. Use search instead.'
          : 'Could not start the camera. Use search instead.'
        );
      });
    });

    return () => { cancelled = true; safeStop(scannerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 360, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Scan barcode</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ padding: 12 }}>
          {error ? (
            <div style={{ fontSize: 13, color: '#f59e0b', padding: '20px 8px', textAlign: 'center' }}>{error}</div>
          ) : (
            <>
              <div id={REGION_ID} style={{ width: '100%', minHeight: 200, borderRadius: 10, overflow: 'hidden', background: 'var(--bg-app)' }} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', marginTop: 10 }}>
                Point the camera at the product barcode.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
